import { useLocation } from '@reach/router';
import { navigate } from 'gatsby';
import { isEmpty } from 'lodash';
import * as React from 'react';
import * as i from 'types';

import { usePrevious } from 'hooks';
import { useQueryUser, useUpdateUser } from 'queries/users';
import {
  checkValid,
  contentSelector,
  isBrowser,
  transformQuestionId,
} from 'services';

export const QuestionnaireContext =
  React.createContext<QuestionnaireContextProps>(
    {} as QuestionnaireContextProps,
  );

export const useQuestionnaireContext = () => {
  const context = React.useContext(QuestionnaireContext);
  if (!context)
    throw new Error(
      'QuestionnaireContext cannot be rendered outside the Questionnaire component',
    );
  return context;
};

type AnswerType = {
  answers: i.TcoAnswers;
  busFinder: i.DecisionAidAnswers;
};

type QuestionnaireKey = 'answers' | 'busFinder';

export const QuestionnaireProvider = ({
  data,
  children,
}: QuestionnaireProviderProps) => {
  const location = useLocation();
  const locationParts = location.pathname.split('/');
  const questionnaireUrl = locationParts[1];
  const isTcoTool = ['questions', 'result', ''].includes(questionnaireUrl);
  const initialPath = isTcoTool ? '/' : '/keuze-hulp/welcome';

  const questionnaire = contentSelector(
    data.allContentfulQuestions,
  ) as i.Question[];
  const busQuestionnaire = contentSelector(
    data.allContentfulQuestionnaireBus,
  ) as i.Question[];
  const fleetOptions = contentSelector(data.allContentfulCars) as i.Car[];

  const questions = isTcoTool ? questionnaire : busQuestionnaire;
  const partner = data.partner;
  const localUserId = isBrowser && localStorage.getItem('userId');
  const resultUrl = isTcoTool ? `/result/${localUserId}` : '/keuze-hulp/result';

  const [userId, setUserId] = React.useState(localUserId || '');
  const [bothAnswers, setBothAnswers] = React.useState<AnswerType>({
    answers: {},
    busFinder: {},
  });
  const [isValid, setIsValid] = React.useState(false);
  const [path, setPath] = React.useState(initialPath);

  const { mutate: updateUser } = useUpdateUser();
  const { data: user } = useQueryUser(userId);

  const isQuestionnaireRoute =
    isTcoTool ||
    ['result', 'keuze-hulp', 'resultaat', 'use-cases'].includes(
      questionnaireUrl,
    );
  const prevQuestionnaireRoute = usePrevious(isQuestionnaireRoute);
  const updateKey = isTcoTool ? 'answers' : 'busFinder';
  const answers = bothAnswers[updateKey];

  const visibleQuestions = React.useMemo(
    () =>
      questions.filter((question: i.Question) => {
        // If the prop is falsy or have no conditions, the question should be visible
        if (!question.isVisible) return true;

        // @ts-ignore
        const answer = answers?.[
          transformQuestionId(question.isVisible.questionId)
        ] as string;
        // Walk through all visibility conditions if they are met
        return question.isVisible.answerValue.includes(answer);
      }),
    [answers],
  );
  const firstQuestionId = visibleQuestions[0].questionId;
  const [current, setCurrent] = React.useState<i.QuestionIds>(firstQuestionId);
  const progress = visibleQuestions.findIndex(
    (question) => question.questionId === current,
  );

  React.useEffect(() => {
    if (localUserId) {
      setUserId(localUserId);
    }
  }, [localUserId]);

  // If user is loaded set the previous answers
  React.useEffect(() => {
    if (user && isEmpty(answers))
      setBothAnswers({
        answers: user.answers,
        busFinder: user.busFinder,
      });
  }, [answers, user]);

  //Remove already given answers for fleet and range if fleet type changes
  React.useEffect(() => {
    if (
      bothAnswers.answers.fleetType &&
      bothAnswers.answers?.fleetType !== 'both'
    ) {
      const filter = bothAnswers.answers?.fleetType === 'car' ? 'van' : 'car';

      // Remove cars and vans from answers.fleet
      bothAnswers.answers?.fleet &&
        (Object.keys(bothAnswers.answers.fleet) as i.KeyOfFleetInput[]).forEach(
          (vehicle) => {
            if (vehicle.startsWith(filter)) {
              delete bothAnswers.answers?.fleet?.[vehicle];
            }
          },
        );

      // Remove cars and vans from answers.range
      bothAnswers.answers?.range &&
        (Object.keys(bothAnswers.answers.range) as i.KeyOfFleetInput[]).forEach(
          (vehicle) => {
            if (vehicle.startsWith(filter)) {
              delete bothAnswers.answers?.range?.[vehicle];
            }
          },
        );

      updateAnswer(bothAnswers.answers, 'answers');
    }
  }, [bothAnswers.answers?.fleetType]);

  // If user goes to questionnaire from contentful url
  // and has already answered some questions
  // navigate to the correct initial url
  React.useEffect(() => {
    if (
      location.pathname === '/questions/type' &&
      isQuestionnaireRoute !== prevQuestionnaireRoute &&
      isQuestionnaireRoute &&
      path !== '/questions/type'
    )
      navigate(path);
  }, [isQuestionnaireRoute]);

  // Update current question and paths when navigating to next question
  // and check wether it is valid already
  React.useEffect(() => {
    if (
      isQuestionnaireRoute === prevQuestionnaireRoute &&
      isQuestionnaireRoute
    ) {
      const questionId =
        locationParts[1] === 'questions' || locationParts[1] === 'keuze-hulp'
          ? (locationParts[locationParts.length - 1] as i.QuestionIds)
          : firstQuestionId;

      const progress = visibleQuestions.findIndex(
        (question) => question.questionId === questionId,
      );
      setIsValid(Boolean(checkValid(visibleQuestions, progress, answers)));
      setCurrent(questionId);
      setPath(
        visibleQuestions[progress]
          ? `${questionnaireUrl}/${visibleQuestions[progress].questionId}`
          : resultUrl,
      );
    }
  }, [location.pathname, visibleQuestions]);

  const updateAnswer = (answers: i.Answers, key?: QuestionnaireKey) => {
    setBothAnswers({
      ...bothAnswers,
      [key || updateKey]: {
        ...answers,
      },
    });

    setIsValid(Boolean(checkValid(visibleQuestions, progress, answers)));

    if (userId) {
      updateUser({
        id: userId,
        [key || updateKey]: JSON.stringify(answers),
      });
    }
  };

  return (
    <QuestionnaireContext.Provider
      value={{
        current,
        progress,
        questions: visibleQuestions,
        answers,
        updateAnswer,
        isValid,
        fleetOptions,
        path,
        partner,
        questionnaireUrl,
        resultUrl,
        visibleQuestions,
        initialPath,
        isTcoTool,
      }}
    >
      {children}
    </QuestionnaireContext.Provider>
  );
};

type QuestionnaireContextProps = {
  answers?: i.Answers;
  isValid: boolean;
  current: i.QuestionIds;
  progress: number;
  questions?: i.Question[];
  updateAnswer: (answers: i.Answers) => void;
  fleetOptions?: i.Car[];
  path: string;
  partner?: i.Partner;
  questionnaireUrl: string;
  resultUrl: string;
  visibleQuestions: i.Question[];
  initialPath: string;
  isTcoTool: boolean;
};

type QuestionnaireProviderProps = {
  children: React.ReactNode;
  data: {
    allContentfulQuestions: GatsbyTypes.ContentfulQuestionsConnection;
    allContentfulQuestionnaireBus: GatsbyTypes.ContentfulQuestionnaireBusConnection;
    allContentfulCars: GatsbyTypes.ContentfulCarsConnection;
    partner: i.Partner;
  };
};
